import { Component, OnInit } from '@angular/core';
import { List as ImmutableList } from 'immutable';
import { LocalStorageService } from '../cache';
import { SessionStorageService } from '../cache/SessionStorage.service';
import { companyDropdownSearchDelay } from '../constants-types/constants';
import { List } from '../constants-types/PiList.model';
import { Company } from '../core-data-model/Company.model';
import { HashService } from '../core-services';
import { CompaniesDataService } from '../core-services/Companies.dataservice';
import { SyncDecorator } from '../core-utilities/SyncDecorators';
import { InitDataService } from '../system-services';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations/Translation.service';

@Component({
    selector: 'pi-company-dropdown',
    template: `
        @if (!companiesData.isSingleCompany) {
            <div>
                <ok-typeahead
                    class="piNav--CompanyDropdown"
                    [ngModel]="typeaheadDisplayText"
                    (onSelected)="companySelected($event)"
                    [list]="companyPossibilities"
                    (onTextChange)="typeaheadTextChange($event)"
                    (onFocusChange)="typeaheadFocused = $event"
                    [placeholder]="
                        TR.key('changeCompany.pickCompanyPlaceholder')
                    "
                    [searchStyling]="false"
                >
                    <ng-template let-item="item">
                        {{ companyDisplayFunction(item) }}
                    </ng-template>
                </ok-typeahead>
            </div>
        } @else {
            <label class="piNav--CompanyLabel">
                {{ companyDisplayFunction(selectedCompany) }}
            </label>
        }
        <!--Handling the case when the user has access to just one company-->
    `,
    standalone: false
})
export class CompanyDropdownComponent implements OnInit {
    companyPossibilities: List<Company> = ImmutableList();
    selectedCompany: Company;

    // This state is needed for the company dropdown because unlike a normal
    // typeahead we want to default back to the model value if the person searches
    // and doesn't select anything
    typeaheadFocused: boolean;
    filteringCompanies = false;

    constructor(
        public TR: TranslationService,
        public companiesData: CompaniesDataService,
        private userProfile: UserProfileSettingsService,
        private session: SessionStorageService,
        private initData: InitDataService,
        private hashService: HashService,
        private localStorage: LocalStorageService
    ) {}

    ngOnInit() {
        this.companiesData.companiesPromise
            .then(() => {
                this.companyPossibilities = this.companiesData.companies;
                this.selectedCompany = this.setDefaultCompany(
                    this.userProfile.currentCompany
                );
            })
            .catch(() => {
                this.selectedCompany = this.companiesData.firstCompany;
            });
    }

    get typeaheadDisplayText(): string {
        return this.typeaheadFocused
            ? ''
            : this.companyDisplayFunction(this.selectedCompany) + ' \u2228';
    }

    typeaheadTextChange(searchText: string) {
        if (searchText && searchText.trim()) {
            this.filteringCompanies = true;
            this.filterCompanyPossibilities(searchText);
        }
    }

    @SyncDecorator.onlyFireAfterDelayInBeingCalled(companyDropdownSearchDelay)
    private filterCompanyPossibilities(searchText: string) {
        this.companyPossibilities = this.companiesData.companies;
        this.companyPossibilities = this.companyPossibilities.filter((item) =>
            item.name.toUpperCase().includes(searchText.toUpperCase())
        );
        this.filteringCompanies = false;
    }

    companySelected(company: Company): void {
        this.selectedCompany = company;
        this.changeCompany(company);
    }

    companyDisplayFunction(item: Company) {
        if (item) {
            return item.name;
        }
        return '';
    }

    private setDefaultCompany(companyId: string) {
        return this.companiesData.companies.find((company) => {
            if (companyId) {
                return company.id === companyId;
            }
            return company.isDefault;
        });
    }

    private changeCompany(company: Company) {
        if (company.id !== this.userProfile.currentCompany) {
            this.userProfile.setCurrentCompany(company.id).then(async () => {
                await this.storeSelectedCompanyId(company.id);
                this.session.clearAll();
                window.location.href = '/';
            });
        }
    }

    private async storeSelectedCompanyId(companyId: string) {
        const currentUserEmail = this.initData.user.email.toLowerCase();
        const hashedEmail =
            await this.hashService.computeHashHex(currentUserEmail);
        this.localStorage.setItem(`${hashedEmail}-currentCompany`, companyId);
    }
}
